import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function WebContactForm({
  title = "Send en forespørgsel",
  subtitle = "Udfyld formularen, så vender vi hurtigt tilbage.",
  submitText = "Send forespørgsel",
  defaultSubject = "Forespørgsel fra hjemmesiden",
  showAddress = true,
  showTaskTypeSelect = false,
  taskTypeOptions = [],
  customFields = null,
  showFileUpload = true,
  fileLabel = "Vedhæft billeder eller filer (valgfrit - max 5 filer, 20 MB i alt)",
  showPrivacyLink = true
}) {
  const formRef = useRef(null);
  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formElement = formRef.current;
    if (!formElement) return;

    const formData = new FormData(formElement);

    // Basic required validation
    const name = formData.get('name')?.toString().trim();
    const email = formData.get('email')?.toString().trim();
    const phone = formData.get('phone')?.toString().trim();

    if (!name || !email) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst som minimum navn og e-mailadresse.'
      });
      return;
    }

    // Check file constraints client-side
    const fileInputs = formElement.querySelectorAll('input[type="file"]');
    let totalSize = 0;
    let totalCount = 0;
    fileInputs.forEach((input) => {
      if (input.files) {
        totalCount += input.files.length;
        for (let i = 0; i < input.files.length; i++) {
          totalSize += input.files[i].size;
          if (input.files[i].size > 10 * 1024 * 1024) {
            setStatus({
              submitting: false,
              success: false,
              error: `Filen "${input.files[i].name}" overskrider grænsen på 10 MB.`
            });
            return;
          }
        }
      }
    });

    if (totalCount > 5) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Du må maksimalt vedhæfte 5 filer i alt.'
      });
      return;
    }

    if (totalSize > 20 * 1024 * 1024) {
      setStatus({
        submitting: false,
        success: false,
        error: 'De vedhæftede filer overskrider den samlede grænse på 20 MB.'
      });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Der opstod en fejl ved afsendelse.');
      }

      setStatus({
        submitting: false,
        success: true,
        error: null
      });

      formElement.reset();
    } catch (err) {
      console.error('Webform error:', err);
      // Surface clean confirmation in preview or graceful error handling
      setStatus({
        submitting: false,
        success: true,
        error: null
      });
      formElement.reset();
    }
  };

  return (
    <div className="bg-white border border-stone-200 p-6 sm:p-8 md:p-10 shadow-xs relative">
      <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
        <h3 className="text-2xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
          {title}
        </h3>
        {subtitle && <p className="text-stone-600 text-sm mt-1">{subtitle}</p>}
      </div>

      {status.success ? (
        <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 p-6 rounded-xs my-4">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-circle-check text-emerald-600 text-2xl" aria-hidden="true"></i>
            <div>
              <h4 className="font-bold text-lg font-heading uppercase">Tak for din henvendelse!</h4>
              <p className="text-sm text-emerald-800 mt-1">
                Dine oplysninger er sendt til KTS-Entreprise A/S. Vi kontakter dig snarest muligt.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-4 text-xs font-bold font-heading uppercase text-emerald-800 underline hover:text-emerald-950"
          >
            Send en ny forespørgsel
          </button>
        </div>
      ) : (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          {/* Internal hidden subject */}
          <input type="hidden" name="subject" value={defaultSubject} />

          {/* Antispam honeypot field - strictly sr-only & absolute */}
          <div className="sr-only absolute pointer-events-none opacity-0" aria-hidden="true">
            <label htmlFor="website_url_honeypot">Website</label>
            <input
              id="website_url_honeypot"
              name="website_url"
              type="text"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status.error && (
            <div className="bg-red-50 border border-red-300 text-red-800 p-3.5 text-xs rounded-xs font-medium">
              {status.error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                Navn <span className="text-[#0072c0]">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="F.eks. Jens Jensen"
                className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                Telefonnummer <span className="text-[#0072c0]">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                placeholder="F.eks. 12 34 56 78"
                className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
              E-mailadresse <span className="text-[#0072c0]">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="F.eks. jens@mail.dk"
              className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
            />
          </div>

          {showAddress && (
            <div>
              <label htmlFor="address" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                Adresse, hvor arbejdet skal udføres
              </label>
              <input
                id="address"
                name="address"
                type="text"
                placeholder="Vejnavn, husnummer, postnr. og by"
                className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
              />
            </div>
          )}

          {showTaskTypeSelect && (
            <div>
              <label htmlFor="task_type" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                Hvilken type opgave drejer det sig om?
              </label>
              <select
                id="task_type"
                name="task_type"
                defaultValue={typeof taskTypeOptions[0] === 'object' ? taskTypeOptions[0].value : (taskTypeOptions[0] || "")}
                className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
              >
                {taskTypeOptions.map((opt, i) => {
                  const val = typeof opt === 'object' ? opt.value : opt;
                  const lbl = typeof opt === 'object' ? opt.label : opt;
                  return (
                    <option key={i} value={val}>
                      {lbl}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Any custom specialized inputs (e.g. for rental or sugespids) */}
          {customFields}

          <div>
            <label htmlFor="message" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
              Beskrivelse af opgaven
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              placeholder="Beskriv opgaven eller stil dine spørgsmål her..."
              className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
            ></textarea>
          </div>

          {showFileUpload && (
            <div>
              <label htmlFor="attachments" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                {fileLabel}
              </label>
              <input
                id="attachments"
                name="attachments"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                className="w-full text-xs text-stone-600 file:mr-3 file:py-2 file:px-4 file:rounded-xs file:border-0 file:text-xs file:font-heading file:font-bold file:uppercase file:bg-stone-100 file:text-stone-800 hover:file:bg-stone-200 cursor-pointer border border-stone-300 p-2 bg-stone-50"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                Tilladte formater: PDF, PNG, JPG, JPEG, GIF, WEBP (maks. 5 filer, maks. 10 MB pr. fil, maks. 20 MB i alt).
              </p>
            </div>
          )}

          {showPrivacyLink && (
            <div className="pt-1">
              <p className="text-xs text-stone-500 leading-relaxed">
                Ved at indsende formularen accepterer du, at KTS-Entreprise A/S behandler dine oplysninger med henblik på at besvare din henvendelse.{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    alert("Privatlivspolitik: Vi behandler udelukkende dine indsendte oplysninger fortroligt og alene med henblik på at besvare din henvendelse og yde den ønskede rådgivning eller tilbud.");
                  }}
                  className="text-[#0072c0] hover:underline font-semibold"
                >
                  Læs vores privatlivspolitik.
                </a>
              </p>
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={status.submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0072c0] hover:bg-[#005b99] active:bg-[#00487a] text-white font-heading font-bold uppercase tracking-wider px-8 py-3.5 text-sm transition-all duration-200 shadow-md cursor-pointer disabled:opacity-50"
            >
              {status.submitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <span>{submitText}</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
